import { createApi } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import axios from "axios";
import type { AxiosRequestConfig, AxiosError } from "axios";
import { config } from "../config";
import { getToken } from "../helpers/util";

export const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: "" }
  ): BaseQueryFn<
    {
      url: string;
      method?: AxiosRequestConfig["method"];
      data?: AxiosRequestConfig["data"];
      params?: AxiosRequestConfig["params"];
      headers?: AxiosRequestConfig["headers"];
    },
    unknown,
    unknown
  > =>
  async ({ url, method, data, params, headers }) => {
    try {
      const result = await axios({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const baseQueryAPI = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: config.apiUrl,
  }),
  tagTypes: ["Routes", "UserFilter"],
  endpoints(build) {
    return {
      getListingDetails: build.query({
        query: (listId) => ({
          url: "get-listing-details",
          method: "post",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          data: {
            listing_unique_id: listId,
          },
        }),
      }),
      manageLeads: build.query({
        query: (args) => ({
          url: "get-my-listings",
          method: "post",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          data: {
            lead_type: args.leadType,
            filters: args.filters,
          },
          params: {
            per_page: 10,
            page: args.currentPage,
          },
        }),
      }),
      myTeam: build.query({
        query: (search) => ({
          url: "get-users",
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
          data: {
            searchTerm: search,
          },
        }),
      }),
      teamActivity: build.query({
        query: (args) => ({
          url: "get-team-activity",
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
          params: {
            per_page: 10,
            page: args.currentPage,
          },
        }),
      }),
      routes: build.query({
        query: () => ({
          url: "get-routes",
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
        }),
        providesTags: [{ type: "Routes" }],
      }),
      routeProperties: build.query({
        query: (routeId) => ({
          url: "get-route-properties",
          method: "post",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
          params: {
            route_public_id: routeId,
          },
        }),
      }),
      teamAreas: build.query({
        query: () => ({
          url: "get-team-area",
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
        }),
      }),
      teamRoutes: build.query({
        query: () => ({
          url: "get-team-routes",
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
        }),
      }),
      area: build.query({
        query: () => ({
          url: "get-area",
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
        }),
      }),
      userFilter: build.query({
        query: () => ({
          url: "get-filters",
          method: "post",
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${getToken()}`,
          },
        }),
        transformResponse: (response: any) => {
          if (response.data && response.data.price_drop) {
            try {
              response.data.price_drop = JSON.parse(response.data.price_drop);
            } catch (error) {
              console.error("Error parsing price_drop:", error);
              response.data.price_drop = [];
            }
          }
          return response;
        },
        providesTags: () => ["UserFilter"],
      }),
      updateUserFilter: build.mutation({
        query: (filters) => ({
          url: "update-filters",
          method: "post",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          data: filters,
        }),
        // invalidatesTags: ["UserFilter"],
        onQueryStarted: async (patch, { dispatch, queryFulfilled }) => {
          const patchResult = dispatch(
            baseQueryAPI.util.updateQueryData(
              "userFilter",
              "user-filter",
              (draft) => {
                console.log("optimistic updating: ", { patch, draft });
                Object.assign(draft, { data: patch });
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            // patchResult.undo();
          }
        },
      }),
      listFilters: build.query({
        query: (args) => {
          console.log({ args });
          return {
            url: "get-my-listing-filters",
            method: "post",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getToken()}`,
            },
            params: {
              per_page: 10 * args.page,
              page: 1,
            },
            data: {
              lead_type: args.leadType,
              filter: args.filter,
              search: args.search,
            },
          };
        },
      }),
      getClusters: build.query({
        query: (args) => {
          return {
            url: "get-clustered-listings-data",
            method: "post",
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
            params: {
              latitude: args.latitude,
              longitude: args.longitude,
              zoom: args.zoom,
            },
          };
        },
      }),
      createPostMailSettings: build.mutation({
        query: (settings) => ({
          url: "settings/create",
          method: "post",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          data: settings,
        }),
      }),
      getPostMailSettings: build.query({
        query: (args) => {
          return {
            url: `settings/team/${args.team_id}`,
            method: "get",
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          };
        },
      }),
      updateSettings: build.mutation({
        query: (settings) => ({
          url: `settings/update/${settings.id}`,
          method: "post",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          data: settings,
        }),
      }),
    };
  },
});
